// --- Imports ---
import {
  Box,
  Chip,
  Grid,
  Stack,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";

// --- Initial Mount Animations ---
const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] },
  },
};

const chipVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05 + 0.2, duration: 0.4, ease: "easeOut" },
  }),
};

// --- Skills Card Component ---
export default function SkillsCard({ skill }) {
  const theme = useTheme();

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ p: .75 }}>
      <motion.div
        variants={cardVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        style={{ width: "100%", height: "100%" }}
      >
        <Box
          sx={{
            backgroundColor: alpha(theme?.palette?.background?.paper, 0.4),
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            p: 2.5,
            border: `1px solid ${alpha(theme?.palette?.divider, 0.1)}`,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.5s cubic-bezier(0.25, 1, 0.5, 1)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: `radial-gradient(circle at top right, ${alpha(skill?.color, 0.1)}, transparent 60%)`,
              opacity: 0,
              transition: "opacity 0.5s ease",
            },
            "&:hover": {
              transform: "translateY(-8px)",
              borderColor: alpha(skill?.color, 0.4),
              boxShadow: `0 20px 40px -10px ${alpha(theme?.palette?.common?.black, 0.5)}, 0 0 30px -10px ${alpha(skill?.color, 0.3)}`,
              "&::before": { opacity: 1 },
              "& .skill-icon": {
                transform: "scale(1.1) rotate(5deg)",
                boxShadow: `0 0 20px ${alpha(skill?.color, 0.4)}`,
              },
            },
          }}
        >
          {/* --- Header --- */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            mb={3}
            sx={{ position: "relative", zIndex: 2 }}
          >
            <Box
              className="skill-icon"
              sx={{
                width: {xs:44 , lg:60},
                height: {xs:44 , lg:60},
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(skill?.color, 0.1),
                color: skill?.color,
                border: `1px solid ${alpha(skill?.color, 0.2)}`,
                transition: "all 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
              }}
            >
              {skill?.icon}
            </Box>

            <Box>
              <Typography
                // variant="h6"
                fontWeight={800}
                sx={{ letterSpacing: "-0.5px", mb: 0.5 , fontSize:".95rem"}}
              >
                {skill?.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: alpha(theme?.palette?.text?.primary, 0.5),
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontSize:".7rem"
                }}
              >
                {skill?.level}% Proficiency
              </Typography>
            </Box>
          </Stack>

          {/* --- Chips --- */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: .5,
              mb: 4,
              flexGrow: 1,
              position: "relative",
              zIndex: 2,
            }}
          >
            {skill?.items?.map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={chipVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <Chip
                  label={item}
                  sx={{
                    backgroundColor: alpha(
                      theme?.palette?.background?.default,
                      0.5,
                    ),
                    color: theme?.palette?.text?.secondary,
                    border: `1px solid ${alpha(theme?.palette?.divider, 0.1)}`,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 32,
                    px: 1,
                    borderRadius: "8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: alpha(skill?.color, 0.1),
                      color: skill?.color,
                      borderColor: alpha(skill?.color, 0.3),
                      transform: "translateY(-2px)",
                    },
                  }}
                />
              </motion.div>
            ))}
          </Box>

          {/* --- Progress Bar --- */}
          <Box sx={{ position: "relative", zIndex: 2 }}>
            <Box
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha(theme?.palette?.common?.white, 0.05),
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                component={motion.div}
                initial={{ width: 0 }}
                whileInView={{ width: `${skill?.level}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
                sx={{
                  height: "100%",
                  backgroundColor: skill?.color,
                  borderRadius: 4,
                  boxShadow: `0 0 15px ${skill?.color}`,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                    animation: "shimmer 2s infinite linear",
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Grid>
  );
}
